import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { BASE_URL } from "../apis/api";

const RELOAD_TARGET_KEY = "vbot_app_reload_target";
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_DELAY_MS = 2500;

// Baked-in build id (when available). Used only as an initial baseline hint;
// the server response is always the source of truth for change detection.
const CLIENT_BUILD_ID = process.env.REACT_APP_BUILD_ID || null;

async function fetchServerVersion() {
  const url = `${BASE_URL}/app-version`;
  const response = await fetch(url, {
    credentials: "include",
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });

  if (!response.ok) {
    throw new Error(`Version check failed (${response.status})`);
  }

  const data = await response.json();
  return data?.data?.version || data?.version || null;
}

function scheduleReload() {
  toast.success("A new version is available. Updating...", {
    duration: RELOAD_DELAY_MS,
  });

  window.setTimeout(() => {
    window.location.reload();
  }, RELOAD_DELAY_MS);
}

export function useAppVersionCheck() {
  const reloadScheduled = useRef(false);
  // The version this session is running against. Seeded from the first server
  // response we receive so that FE/BE build-time ids never need to match.
  const baselineVersion = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return undefined;
    }

    const handleVersion = (serverVersion) => {
      if (!serverVersion || reloadScheduled.current) {
        return;
      }

      // First time we learn the server version: this is the version the
      // currently-loaded page was served with, so treat it as our baseline.
      if (!baselineVersion.current) {
        baselineVersion.current = serverVersion;

        // Force refresh on load: the user opened the app with a stale cached
        // bundle (its baked id differs from what the server now serves). Only
        // possible when CLIENT_BUILD_ID is a real deploy id that matches the
        // server's scheme (see Dockerfile/CI APP_BUILD_ID wiring). Reload once,
        // guarded so a persistent mismatch can't loop forever.
        if (
          CLIENT_BUILD_ID &&
          CLIENT_BUILD_ID !== serverVersion &&
          localStorage.getItem(RELOAD_TARGET_KEY) !== serverVersion
        ) {
          reloadScheduled.current = true;
          localStorage.setItem(RELOAD_TARGET_KEY, serverVersion);
          scheduleReload();
          return;
        }

        // A successful load against this version means any previous reload
        // attempt is resolved; clear the loop guard.
        if (
          !CLIENT_BUILD_ID ||
          CLIENT_BUILD_ID === serverVersion ||
          localStorage.getItem(RELOAD_TARGET_KEY) === serverVersion
        ) {
          localStorage.removeItem(RELOAD_TARGET_KEY);
        }
        return;
      }

      // Server still reports the version this session started with: up to date.
      if (baselineVersion.current === serverVersion) {
        localStorage.removeItem(RELOAD_TARGET_KEY);
        return;
      }

      // Server advertises a genuinely different version => a new deploy shipped
      // while this tab was open. Reload once to pick it up.
      //
      // Loop guard: if we already reloaded for this exact server version and
      // we're somehow seeing the mismatch again, another reload won't fix it
      // (misconfig / stale build), so stop instead of reloading forever.
      if (localStorage.getItem(RELOAD_TARGET_KEY) === serverVersion) {
        baselineVersion.current = serverVersion;
        return;
      }

      reloadScheduled.current = true;
      localStorage.setItem(RELOAD_TARGET_KEY, serverVersion);
      scheduleReload();
    };

    const checkVersion = async () => {
      try {
        const serverVersion = await fetchServerVersion();
        handleVersion(serverVersion);
      } catch {
        // Ignore transient network errors during background checks.
      }
    };

    checkVersion();

    const intervalId = window.setInterval(checkVersion, CHECK_INTERVAL_MS);
    const onFocus = () => {
      checkVersion();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
